import { useEffect, useRef, useState } from 'react'

/*
 * Zwei Schübe zu drei Blinkern, dazwischen fünf Sekunden Pause. Danach ist
 * Ruhe — ein dauerhaft blinkendes Element liest man nach einer Minute nicht
 * mehr, man sieht darüber hinweg.
 */
const SCHUEBE = 2
const PAUSE = 5000

/*
 * Notbremse, falls `animationend` ausbleibt.
 *
 * Drei Blinker zu 0,9 s dauern 2,7 s; danach ist reichlich Luft. Gebraucht wird
 * das, weil eine Animation nicht immer läuft: In einem Hintergrund-Tab bekommt
 * die Seite keine Frames, und ohne Frames gibt es kein `animationend`. Ohne
 * diese Bremse bliebe die Klasse hängen — und beim Zurückwechseln stünde da ein
 * dauerhaft orange leuchtender Knopf.
 */
const NOTBREMSE = 4000

/**
 * Der Name der Animation aus `styles.scss`.
 *
 * Die Keyframes stehen global und nicht in einem SCSS-Modul: Ein Modul würde
 * sie in jeder einbindenden Datei erneut erzeugen, und der Name wäre je Datei
 * ein anderer — dann ließe sich unten nicht mehr prüfen, welche Animation da
 * gerade zu Ende ging.
 */
export const PULS_ANIMATION = 'junior-attention-pulse'

/**
 * Die Klasse, die das Leuchten anschaltet — global, aus `styles.scss`.
 *
 * Kein SCSS-Modul: Module benennen auch Animationsnamen um, und zwar selbst
 * dann, wenn die Keyframes woanders stehen. Die Animation lief dann zwar, hatte
 * aber keine Keyframes und bewirkte nichts.
 */
export const PULS_KLASSE = 'junior-attention-pulse'

// Wer Bewegung abbestellt hat, bekommt keine. Das Leuchten ist ein Hinweis,
// kein Inhalt — es fällt ersatzlos weg.
const wenigerBewegung = () =>
	typeof window !== 'undefined' &&
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Lässt etwas kurz die Aufmerksamkeit auf sich ziehen — und dann aufhören.
 *
 * Gebraucht für aktive Filter: Eine Liste, die nur einen Ausschnitt zeigt, sieht
 * aus wie eine kurze Liste. Wer von einer anderen Seite zurückkommt (die Filter
 * werden pro Browser gemerkt, siehe `UI/FilterPanel/useFilters`), sucht dann
 * Einträge, die es scheinbar nicht mehr gibt — im schlimmsten Fall steht „Keine
 * Anforderung passt zu Suche und Filtern" und man hält die Ansicht für kaputt.
 *
 * **Ausgelöst wird nur beim Übergang von „kein Filter" zu „Filter"** (und beim
 * Anzeigen, wenn schon einer steht). Wer gerade selbst den zweiten Filter
 * setzt, weiß Bescheid; ein Blinken bei jeder Änderung wäre Zappeln statt
 * Hinweis.
 *
 * Ein Schub endet nicht nach der Uhr, sondern über `onAnimationEnd` — so
 * bleiben Klasse und Bewegung im Takt, auch wenn der Browser die Seite
 * zwischendurch anhält (anderer Tab).
 *
 * @param {boolean} aktiv Schränkt gerade etwas die Ansicht ein?
 * @returns {{leuchtet: boolean, onAnimationEnd: (event) => void}}
 */
export default function useAttentionPulse(aktiv) {
	// 0 = noch nichts gelaufen · 1..SCHUEBE = leuchtet gerade · -1 = fertig
	const [schub, setSchub] = useState(0)
	const timer = useRef(null)
	const bremse = useRef(null)

	useEffect(() => {
		if (aktiv && !wenigerBewegung()) {
			// Nur der Übergang zählt: Ist schon ein Schub gelaufen (oder läuft),
			// bleibt es dabei, bis die Filter einmal ganz weg waren.
			setSchub(bisher => (bisher === 0 ? 1 : bisher))
			return
		}

		clearTimeout(timer.current)
		clearTimeout(bremse.current)
		setSchub(0)
	}, [aktiv])

	// Läuft ein Schub, wird die Notbremse gespannt: Bleibt `animationend` aus,
	// beendet sie ihn von sich aus.
	useEffect(() => {
		if (schub <= 0) return undefined

		bremse.current = setTimeout(() => beendeSchub(), NOTBREMSE)
		return () => clearTimeout(bremse.current)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [schub])

	// Ein Timer, der nach dem Aushängen feuert, schriebe in eine Komponente, die
	// es nicht mehr gibt.
	useEffect(
		() => () => {
			clearTimeout(timer.current)
			clearTimeout(bremse.current)
		},
		[]
	)

	const beendeSchub = () => {
		clearTimeout(bremse.current)

		if (schub >= SCHUEBE) {
			setSchub(-1)
			return
		}

		const naechster = schub + 1
		setSchub(-1)
		timer.current = setTimeout(() => setSchub(naechster), PAUSE)
	}

	const onAnimationEnd = event => {
		/*
		 * Nur die eigene Animation weiterzählen. Ein Kind mit eigener Bewegung
		 * (ein Zähler, der sich einblendet) meldet sich sonst hier und bricht
		 * den Takt ab.
		 *
		 * Abgewiesen wird aber nur, was sich positiv als etwas anderes ausweist.
		 * Fehlt der Name ganz, wird weitergezählt: Lieber einmal zu früh
		 * aufhören als ewig weiterleuchten, weil eine Umgebung das Feld nicht
		 * füllt (jsdom kennt `AnimationEvent` gar nicht).
		 */
		if (event.animationName && event.animationName !== PULS_ANIMATION) return

		beendeSchub()
	}

	return { leuchtet: schub > 0, onAnimationEnd }
}
